import { Flex, SegmentedControl } from '@mantine/core';
import { WorklogIcon } from '@/views/map/worklog/WorklogIcon/WorklogIcon';
import { getWorklogStatusText } from '@/features/types';
import { WorkLogStatus } from '@/generated/types/navigator/types.gen';
import classes from './WorklogStatusSelector.module.css';
import slugify from 'slugify';

interface WorklogStatusSelectorProps {
  value: WorkLogStatus | undefined;
  onChange: (value: WorkLogStatus) => void;
}

export const WorklogStatusSelector = ({
  value,
  onChange,
}: WorklogStatusSelectorProps) => {
  const optionWithIcons = (workStatus: WorkLogStatus) => {
    const testId = `worklog-status-${slugify(workStatus, { lower: true })}`;
    return (
      <Flex w="100%" align="center" gap="xs" mih={48} data-testid={testId}>
        <WorklogIcon workStatus={workStatus} />
        <span>{getWorklogStatusText(workStatus)}</span>
      </Flex>
    );
  };

  const statuses = [
    WorkLogStatus.COMPLETED,
    WorkLogStatus.NO_WORK_NEEDED,
    WorkLogStatus.WORK_NEEDED,
  ];

  const options = statuses.map((status) => ({
    value: status,
    label: optionWithIcons(status),
  }));

  return (
    <div
      className={classes.selectorWrapper}
      data-testid="worklog-status-selector"
    >
      <SegmentedControl
        classNames={{
          root: classes.root,
          label: classes.label,
          indicator: classes.indicator,
        }}
        // An empty string keeps the control controlled while no status is
        // chosen. Passing undefined makes Mantine treat it as uncontrolled,
        // and the first choice is then lost.
        value={value ?? ''}
        onChange={(selected) => {
          const status = statuses.find((s) => s === selected);
          if (status) onChange(status);
        }}
        data={options}
        fullWidth
        orientation="vertical"
        withItemsBorders={false}
        size="xl"
        transitionDuration={0}
      />
    </div>
  );
};
